import { useState, useCallback } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { PrincipalDocumentosLista } from "../../../auditoria/domain/models/documentos_lista";
import { useAuditoresPagination } from "../../../auditoria/presentation/hooks/Auditoria_auditores/pagination";
import { DashboardControleDocumentos } from "../../domain/models/dashboard_controle_documentos";
import { initialGedInternosFilterState, useGedInternosFilter } from "../hooks/GED_internos/filter";
import { useGedInternosPagination } from "../hooks/GED_internos/pagination";
import { initialGedExternosFilterState, useGedExternosFilter } from "../hooks/GED_externos/filter";
import { useGedExternosPagination } from "../hooks/GED_externos/pagination";

import CommonConstants from "../../../../common/constants";

/**
 * LoadPrincipalListaFuncState
 */

type LoadPrincipalListaFuncState = {
  IdArquivoModulo?: number;
  page?: number;
  rowsPerPage?: number;
  tipoPesquisa?: number,
  pesquisa?: string,
};


/**
 * usePrincipalControleDocumentosViewModel
 */

export const usePrincipalControleDocumentosViewModel = () => {
  const {
    commons: {
      getAllListagemDocUseCase,
      getGedArquivoDataUseCase,
    },
    controleDocumentos: {
      getDashboardControleDocumentosInformationUseCase,
    },
  } = useDependencyInjection();

  const { pushErrorNotification } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [dashboardControleDocumentosInformation, setDashboardControleDocumentosInformation] = useState<DashboardControleDocumentos>();

  const [docLista, setDocLista] = useState<PrincipalDocumentosLista[]>([]);

  const pagination = useAuditoresPagination();

  /**
   * Funcoes de carregamento
   */

  const loadDashboardControleDocumentosInformation = useCallback(async () => {
    try {
      const result = await getDashboardControleDocumentosInformationUseCase.getDashboardControleDocumentosInformation();
      setDashboardControleDocumentosInformation(result);
    } catch (e: unknown) {
      setDashboardControleDocumentosInformation(undefined);
      throw new Error("Falha ao buscar informações do dashboard!");
    }
  }, [
    getDashboardControleDocumentosInformationUseCase,
  ]);

  const loadDocListaState = useCallback(async (funcState?: LoadPrincipalListaFuncState) => {
    try {
      const filteredPage = funcState?.page ?? pagination.page;
      const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;
      await getAllListagemDocUseCase.getAllListagemDoc(
        String(CommonConstants.moduloDocumentos.EntregaObras),
        filteredPage,
        filteredRowsPerPage
      );
      setDocLista(getAllListagemDocUseCase.items);
      pagination.setRowsPerPage(getAllListagemDocUseCase.pageSize);
      pagination.setTotalItems(getAllListagemDocUseCase.totalItems);
    } catch (e: unknown) {
      throw new Error("Falha ao buscar documentos!");
    }
  }, [
    getAllListagemDocUseCase,
    pagination,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      await loadDashboardControleDocumentosInformation();
      await loadDocListaState();
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao carregar os dados. Tente novamente.')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage(undefined);
      await loadDashboardControleDocumentosInformation();
      await loadDocListaState();
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao recarregar os dados. Tente novamente.'));
    } finally {
      setLoading(false);
    }
  }, [
    loadDashboardControleDocumentosInformation,
    loadDocListaState,
    pushErrorNotification,
  ]);

  const handleCardClick = useCallback((cardType: string) => {
    switch (cardType) {
      case 'documentosInternosAtualizados':
        useGedInternosFilter.setState({ ...initialGedInternosFilterState, status: 2 });
        useGedInternosPagination.setState({ page: 1 });
        backOrNavigate('/GED_internos');
        break;

      case 'documentosInternosObsoletos':
        useGedInternosFilter.setState({ ...initialGedInternosFilterState, status: 3 });
        useGedInternosPagination.setState({ page: 1 });
        backOrNavigate('/GED_internos');
        break;

      case 'documentosExternosConferenciaAtualizacao':
        useGedExternosFilter.setState({ ...initialGedExternosFilterState, status: 5 });
        useGedExternosPagination.setState({ page: 1 });
        backOrNavigate('/GED_externos');
        break;

      case 'documentosExternosAtualizados':
        useGedExternosFilter.setState({ ...initialGedExternosFilterState, status: 2 });
        useGedExternosPagination.setState({ page: 1 });
        backOrNavigate('/GED_externos');
        break;

      case 'documentosExternosVencidos':
        useGedExternosFilter.setState({ ...initialGedExternosFilterState, status: 4 });
        useGedExternosPagination.setState({ page: 1 });
        backOrNavigate('/GED_externos');
        break;

      case 'documentosExternosObsoletos':
        useGedExternosFilter.setState({ ...initialGedExternosFilterState, status: 3 });
        useGedExternosPagination.setState({ page: 1 });
        backOrNavigate('/GED_externos');
        break;
    }
  }, [
    backOrNavigate,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      pagination.setPage(page + 1);;
      await loadDocListaState({ page: page + 1, });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    pagination,
    loadDocListaState,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      pagination.setPage(1);
      pagination.setRowsPerPage(rowsPerPage);

      await loadDocListaState({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    pagination,
    loadDocListaState,
    pushErrorNotification,
  ]);

  const handleDownloadFile = useCallback(async (IdArquivoModulo: number, NomeArquivo: string) => {
    if (IdArquivoModulo > 0) {
      try {
        setLoading(true);
        await getGedArquivoDataUseCase
          .getGedArquivoData(IdArquivoModulo, NomeArquivo);
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar documentos!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    getGedArquivoDataUseCase,
    pushErrorNotification,
  ]);

  return {
    loading,
    errorMessage,
    dashboardControleDocumentosInformation: dashboardControleDocumentosInformation ?? null,
    docLista,
    pagination,
    handleReload,
    handleCardClick,
    handlePageChange,
    handleRowsPerPageChange,
    handleDownloadFile,
  };
}